/**
 * Staff Session Manager - Olympic Fit Wellness
 * Allows staff to create, edit, and manage their sessions
 */
import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { format, addHours, setHours, setMinutes } from 'date-fns';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  DollarSign,
  X,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Bell
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

// Session type configuration
const SESSION_TYPES = [
  { value: 'group_class', label: 'Group Class', color: '#3B82F6' },
  { value: 'one_on_one', label: '1-on-1 Session', color: '#10B981' },
  { value: 'workshop', label: 'Workshop', color: '#8B5CF6' }
];

export default function StaffSessionManager() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    session_type: 'group_class',
    service_category: '',
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    max_participants: 10,
    price: 0,
    included_in_membership: true
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch data
  const fetchSessions = useCallback(async () => {
    try {
      const data = await api.getCalendarSessions({ staff_id: user?.id });
      setSessions(data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, [user?.id]);

  const fetchServiceCategories = async () => {
    try {
      const data = await api.getServiceCategories();
      setServiceCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const data = await api.getPendingBookingRequests();
      setPendingRequests(data);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSessions(),
        fetchServiceCategories(),
        fetchPendingRequests()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchSessions]);

  // Handle date click (create new session)
  const handleDateClick = (dateInfo) => {
    const startDate = dateInfo.date;
    const endDate = addHours(startDate, 1);
    
    setSelectedDate(startDate);
    setFormData({
      ...formData,
      title: '',
      description: '',
      start_time: format(startDate, "yyyy-MM-dd'T'HH:mm"),
      end_time: format(endDate, "yyyy-MM-dd'T'HH:mm")
    });
    setShowCreateModal(true);
  };

  // Handle event click (edit session)
  const handleEventClick = (clickInfo) => {
    const session = clickInfo.event;
    setSelectedSession({
      id: session.id,
      ...session.extendedProps,
      title: session.title,
      start: session.start,
      end: session.end
    });
    setFormData({
      title: session.title,
      description: session.extendedProps?.description || '',
      session_type: session.extendedProps?.session_type || 'group_class',
      service_category: session.extendedProps?.service_category || '',
      start_time: format(session.start, "yyyy-MM-dd'T'HH:mm"),
      end_time: format(session.end, "yyyy-MM-dd'T'HH:mm"),
      duration_minutes: session.extendedProps?.duration_minutes || 60,
      max_participants: session.extendedProps?.max_participants || 10,
      price: session.extendedProps?.price || 0,
      included_in_membership: session.extendedProps?.included_in_membership ?? true
    });
    setShowEditModal(true);
  };

  // Calculate duration when times change
  const handleTimeChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    if (newData.start_time && newData.end_time) {
      const start = new Date(newData.start_time);
      const end = new Date(newData.end_time);
      newData.duration_minutes = Math.round((end - start) / 60000);
    }
    
    setFormData(newData);
  };

  // Create session
  const handleCreateSession = async () => {
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    try {
      const result = await api.createCalendarSession(formData);
      if (result.success) {
        toast.success('Session created!');
        setShowCreateModal(false);
        fetchSessions();
        resetForm();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create session');
    } finally {
      setFormLoading(false);
    }
  };

  // Update session
  const handleUpdateSession = async () => {
    if (!selectedSession?.id) return;

    setFormLoading(true);
    try {
      const result = await api.updateCalendarSession(selectedSession.id, {
        title: formData.title,
        description: formData.description,
        start_time: formData.start_time,
        end_time: formData.end_time,
        max_participants: formData.max_participants,
        price: formData.price
      });
      if (result.success) {
        toast.success('Session updated!');
        setShowEditModal(false);
        fetchSessions();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update session');
    } finally {
      setFormLoading(false);
    }
  };

  // Cancel session
  const handleCancelSession = async () => {
    if (!selectedSession?.id) return;
    
    if (!confirm('Are you sure you want to cancel this session? All bookings will be cancelled.')) {
      return;
    }

    setFormLoading(true);
    try {
      const result = await api.cancelCalendarSession(selectedSession.id, 'Cancelled by staff');
      toast.success(`Session cancelled. ${result.affected_bookings || 0} booking(s) affected.`);
      setShowEditModal(false);
      fetchSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to cancel session');
    } finally {
      setFormLoading(false);
    }
  };

  // Respond to booking request
  const handleRespondToRequest = async (bookingId, status, message = null) => {
    try {
      await api.respondToBooking(bookingId, status, message);
      toast.success(`Request ${status}`);
      fetchPendingRequests();
      fetchSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to respond');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      session_type: 'group_class',
      service_category: '',
      start_time: '',
      end_time: '',
      duration_minutes: 60,
      max_participants: 10,
      price: 0,
      included_in_membership: true
    });
  };

  // Session form component
  const SessionForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Title *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Morning Yoga, Personal Training"
          />
        </div>

        <div>
          <Label>Session Type</Label>
          <Select
            value={formData.session_type}
            onValueChange={(value) => setFormData({ ...formData, session_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SESSION_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Service Category</Label>
          <Select
            value={formData.service_category}
            onValueChange={(value) => setFormData({ ...formData, service_category: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {serviceCategories.map((cat) => (
                <SelectItem key={cat.name} value={cat.name}>
                  {cat.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Start Time *</Label>
          <Input
            type="datetime-local"
            value={formData.start_time}
            onChange={(e) => handleTimeChange('start_time', e.target.value)}
          />
        </div>

        <div>
          <Label>End Time *</Label>
          <Input
            type="datetime-local"
            value={formData.end_time}
            onChange={(e) => handleTimeChange('end_time', e.target.value)}
          />
        </div>

        <div>
          <Label>Max Participants</Label>
          <Input
            type="number"
            min="1"
            value={formData.max_participants}
            onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 1 })}
          />
        </div>

        <div>
          <Label>Price ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
          />
        </div>

        <div className="col-span-2">
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what participants can expect..."
            rows={3}
          />
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <Switch
            checked={formData.included_in_membership}
            onCheckedChange={(checked) => setFormData({ ...formData, included_in_membership: checked })}
          />
          <Label>Included in membership</Label>
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={formLoading} className="bg-blue-600 hover:bg-blue-700">
          {formLoading ? 'Saving...' : submitLabel}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold">Session Manager</h1>
        </div>
        <div className="flex gap-2">
          {pendingRequests.length > 0 && (
            <Button 
              variant="outline"
              onClick={() => setShowRequestsModal(true)}
              className="relative"
            >
              <Bell className="w-4 h-4 mr-2" />
              Pending Requests
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {pendingRequests.length}
              </span>
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            New Session
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={sessions}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            selectable={true}
            editable={false}
            slotMinTime="06:00:00"
            slotMaxTime="21:00:00"
            allDaySlot={false}
            nowIndicator={true}
            height="auto"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
          />
        )}
      </div>

      {/* Create Session Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Session</DialogTitle>
            <DialogDescription>
              Add a class, 1-on-1 session, or workshop to your calendar
            </DialogDescription>
          </DialogHeader>
          <SessionForm onSubmit={handleCreateSession} submitLabel="Create Session" />
        </DialogContent>
      </Dialog>

      {/* Edit Session Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Session</DialogTitle>
            <DialogDescription>
              {selectedSession?.current_participants > 0 && (
                <span className="text-amber-600">
                  ⚠️ {selectedSession.current_participants} participant(s) booked
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <SessionForm onSubmit={handleUpdateSession} submitLabel="Save Changes" />
          <div className="border-t pt-4 mt-4">
            <Button 
              variant="destructive" 
              onClick={handleCancelSession}
              disabled={formLoading}
              className="w-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Cancel Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Requests Modal */}
      <Dialog open={showRequestsModal} onOpenChange={setShowRequestsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Pending Booking Requests</DialogTitle>
            <DialogDescription>
              Approve or decline 1-on-1 session requests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {pendingRequests.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No pending requests</p>
            ) : (
              pendingRequests.map((request) => (
                <div key={request.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{request.profiles?.full_name || 'Member'}</p>
                      <p className="text-sm text-gray-500">{request.profiles?.email}</p>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                      Pending
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p><strong>Session:</strong> {request.calendar_sessions?.title}</p>
                    <p><strong>Date:</strong> {request.calendar_sessions?.start_time && 
                      format(new Date(request.calendar_sessions.start_time), 'PPp')}</p>
                  </div>
                  {request.request_message && (
                    <div className="bg-gray-50 p-2 rounded text-sm">
                      <p className="text-gray-500 text-xs mb-1">Message:</p>
                      {request.request_message}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleRespondToRequest(request.id, 'confirmed')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleRespondToRequest(request.id, 'declined')}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
